import {
  applyColorPair,
  createProfile,
  defaultRng,
  ensureCurrentText,
  normalizeImportedProfile,
  recordAndPickNext,
} from "../shared/algorithm.js";
import { corpus, corpusById } from "../shared/corpus.js";
import { syncAchievements } from "../shared/achievements.js";
import { syncLocations } from "../shared/location.js";
import { SPELLING_CURRICULUM } from "../shared/spelling-curriculum.js";
import { ShareRecord, StudentSummary, SyncOperation } from "../shared/api.js";
import {
  MathQuestion,
  mathLevelReady,
  questionForLevel,
  questionFromPair,
  questionWithHint,
  updateMathHint,
} from "../shared/math.js";
import { lifetimeCoins, shopItemById } from "../shared/shop-items.js";
import { normalizeSubjectProgress } from "../shared/storage.js";
import { clearState, loadState, loadStateResult, saveState, StorageLike } from "../shared/storage.js";
import {
  AppState,
  IncomingShare,
  INSTRUCTOR_UNLOCKED_KEY,
  PASSCODE_LENGTH,
  Profile,
  ReadingBand,
  ReadingText,
  ResultKind,
  Subject,
  SubjectProgress,
} from "../shared/type.app.js";
import { createId, nextLevelEpoch } from "../shared/util.id.js";

const applySummaryLevels = (
  progress: NonNullable<Profile["subjectProgress"]>,
  student: StudentSummary,
): NonNullable<Profile["subjectProgress"]> => {
  const next = { ...progress };
  for (const subject of ["reading", "math", "spelling"] as const) {
    const remote = student.subjects[subject];
    if (!remote?.tracked) continue;
    next[subject] = {
      ...next[subject],
      level: remote.level,
      boostLevel: remote.boostLevel,
      correctAnswers: remote.correctAnswers,
      currentTextId: remote.currentTextId,
      levelEpoch: remote.levelEpoch,
    };
  }
  return next;
};

const withLifetimeCoins = (profile: Profile): Profile => {
  const coinsEarned = lifetimeCoins(profile.coins, profile.inventory, profile.coinsEarned);
  return coinsEarned === profile.coinsEarned ? profile : { ...profile, coinsEarned };
};

const withProgress = (profile: Profile): Profile => {
  return syncLocations(syncAchievements(withLifetimeCoins(profile)));
};

const awardCoins = (profile: Profile, credit: number): { profile: Profile; awardedCoins: number } => {
  if (credit <= 0) return { profile, awardedCoins: 0 };
  let until = profile.correctsUntilCoin;
  if (until <= 0) until = defaultRng.int(3, 6);
  until -= credit;
  if (until > 0) return { profile: { ...profile, correctsUntilCoin: until }, awardedCoins: 0 };
  let untilBonus = profile.coinAwardsUntilBonus;
  if (untilBonus <= 0) untilBonus = defaultRng.int(10, 15);
  untilBonus -= 1;
  const awardedCoins = untilBonus <= 0 ? defaultRng.int(2, 5) : 1;
  return {
    profile: {
      ...profile,
      coins: profile.coins + awardedCoins,
      coinsEarned: profile.coinsEarned + awardedCoins,
      correctsUntilCoin: defaultRng.int(3, 6),
      coinAwardsUntilBonus: untilBonus <= 0 ? defaultRng.int(10, 15) : untilBonus,
    },
    awardedCoins,
  };
};

export class AppStore extends EventTarget {
  state: AppState;
  instructorUnlocked = false;
  sessionUnlocked = false;
  private readonly storage: StorageLike;

  private studentCreationOperation(profile: Profile): SyncOperation {
    return {
      eventId: createId(),
      type: "student.created",
      studentId: profile.id,
      occurredAt: profile.createdAt,
      payload: { name: profile.name, colorPairIndex: profile.colorPairIndex, readingBand: profile.band },
    };
  }

  private currentItemOperations(profile: Profile): SyncOperation[] {
    return (
      [
        ["reading", profile.currentTextId],
        ["math", profile.subjectProgress?.math.currentTextId ?? null],
        ["spelling", profile.spellingWordId ?? null],
      ] as const
    ).map(([subject, currentTextId]) => ({
      eventId: createId(),
      type: "subject.current.updated" as const,
      studentId: profile.id,
      occurredAt: new Date().toISOString(),
      payload: { subject, currentTextId },
    }));
  }

  private profileCreationOperations(profile: Profile): SyncOperation[] {
    return [this.studentCreationOperation(profile), ...this.currentItemOperations(profile)];
  }

  constructor(storage: StorageLike) {
    super();
    this.storage = storage;
    const loadedResult = loadStateResult(storage);
    const loaded = loadedResult.state;
    this.state = {
      ...loaded,
      profiles: loaded.profiles.map((profile) => ensureCurrentText(withProgress(profile), corpus)),
    };
    this.instructorUnlocked = storage.getItem(INSTRUCTOR_UNLOCKED_KEY) === "1";
    if (loadedResult.valid) this.persist();
  }

  get currentProfile(): Profile | null {
    return this.state.profiles.find((profile) => profile.id === this.state.currentProfileId) ?? null;
  }

  get currentText(): ReadingText | null {
    const profile = this.currentProfile;
    if (!profile?.currentTextId) {
      return null;
    }
    return corpusById[profile.currentTextId] ?? null;
  }

  get selectedSubject(): Subject {
    return this.currentProfile?.selectedSubject ?? "reading";
  }

  subjectProgress(subject: Subject = this.selectedSubject): SubjectProgress | null {
    return this.currentProfile?.subjectProgress?.[subject] ?? null;
  }

  setSelectedSubject(subject: Subject): void {
    const profile = this.currentProfile;
    if (!profile || profile.selectedSubject === subject) return;
    this.replaceProfile({ ...profile, selectedSubject: subject });
  }

  setSpellingWord(wordId: string): void {
    const profile = this.currentProfile;
    if (!profile || profile.spellingWordId === wordId) return;
    this.setCurrentItem("spelling", wordId);
  }

  setCurrentItem(subject: Subject, currentTextId: string | null): void {
    const profile = this.currentProfile;
    if (!profile || this.isIncomingShare(profile.id)) return;
    const normalized = normalizeSubjectProgress(profile);
    const current =
      subject === "reading"
        ? profile.currentTextId
        : subject === "math"
          ? normalized.subjectProgress?.math.currentTextId
          : (profile.spellingWordId ?? null);
    if (current === currentTextId) return;
    const next =
      subject === "reading"
        ? { ...profile, currentTextId }
        : subject === "math"
          ? {
              ...normalized,
              subjectProgress: {
                ...normalized.subjectProgress!,
                math: { ...normalized.subjectProgress!.math, currentTextId },
              },
            }
          : { ...normalized, spellingWordId: currentTextId ?? undefined };
    this.replaceProfile(next, "settingsChanged", [
      {
        eventId: createId(),
        type: "subject.current.updated",
        studentId: profile.id,
        occurredAt: new Date().toISOString(),
        payload: { subject, currentTextId },
      },
    ]);
  }

  private persist(
    skipCloud = false,
    reason?: "profileSwitch" | "answerRecorded" | "settingsChanged",
    operations: SyncOperation[] = [],
  ): void {
    saveState(this.storage, this.state);
    this.dispatchEvent(new Event("change"));
    this.dispatchEvent(new CustomEvent("persist", { detail: { skipCloud, reason, operations } }));
  }

  private replaceProfile(
    next: Profile,
    reason?: "profileSwitch" | "answerRecorded" | "settingsChanged",
    operations: SyncOperation[] = [],
  ): void {
    const synced = withProgress(next);
    this.state = {
      ...this.state,
      profiles: this.state.profiles.map((profile) => (profile.id === synced.id ? synced : profile)),
    };
    this.persist(false, reason, operations);
  }

  createFirstProfile(name: string, band: ReadingBand): void {
    const profile = ensureCurrentText(withProgress(createProfile(name, band, [])), corpus);
    this.state = {
      ...this.state,
      profiles: [profile],
      currentProfileId: profile.id,
    };
    this.persist(false, undefined, this.profileCreationOperations(profile));
  }

  addProfile(name: string, band: ReadingBand, colorPairIndex?: number): Profile {
    const used = this.state.profiles.map((profile) => profile.colorPairIndex);
    const profile = ensureCurrentText(
      withProgress(createProfile(name, band, used, new Date(), colorPairIndex)),
      corpus,
    );
    this.state = {
      ...this.state,
      profiles: [...this.state.profiles, profile],
      currentProfileId: profile.id,
    };
    this.persist(false, undefined, this.profileCreationOperations(profile));
    return profile;
  }

  renameProfile(id: string, name: string): void {
    const profile = this.state.profiles.find((item) => item.id === id);
    if (!profile) return;
    const nextName = name.trim() || profile.name;
    this.replaceProfile({ ...profile, name: nextName }, "settingsChanged", [
      {
        eventId: createId(),
        type: "student.metadata.updated",
        studentId: profile.id,
        occurredAt: new Date().toISOString(),
        payload: { name: nextName },
      },
    ]);
  }

  recolorProfile(id: string, colorPairIndex: number): void {
    const profile = this.state.profiles.find((item) => item.id === id);
    if (!profile) return;
    this.replaceProfile(applyColorPair(profile, colorPairIndex), "settingsChanged", [
      {
        eventId: createId(),
        type: "student.metadata.updated",
        studentId: profile.id,
        occurredAt: new Date().toISOString(),
        payload: { colorPairIndex },
      },
    ]);
  }

  setProfileLevel(id: string, level: number): void {
    this.setSubjectLevel(id, "reading", level);
  }

  setSubjectLevel(id: string, subject: Subject, level: number): void {
    const profile = this.state.profiles.find((item) => item.id === id);
    if (!profile) return;
    const normalized = normalizeSubjectProgress(profile);
    if (!normalized.subjectProgress) return;
    const levelEpoch = nextLevelEpoch();
    const nextProgress = {
      ...normalized.subjectProgress[subject],
      level,
      boostLevel: level,
      correctStreak: 0,
      wrongStreak: 0,
      currentTextId: null,
      levelEpoch,
    };
    const next = {
      ...normalized,
      manuallySetLevel: true,
      subjectProgress: { ...normalized.subjectProgress, [subject]: nextProgress },
    };
    const adjusted =
      subject === "reading"
        ? ensureCurrentText(
            {
              ...next,
              level,
              boostLevel: level,
              boostActive: false,
              correctStreak: 0,
              wrongStreak: 0,
              currentTextId: null,
            },
            corpus,
          )
        : subject === "spelling"
          ? { ...next, spellingWordId: undefined }
          : next;
    this.replaceProfile(adjusted, "settingsChanged", [
      {
        eventId: createId(),
        type: "level.adjusted",
        studentId: profile.id,
        occurredAt: new Date().toISOString(),
        payload: { subject, level, levelEpoch, reason: "Instructor adjustment" },
      },
    ]);
  }

  incomingShareFor(id: string): IncomingShare | null {
    return this.state.incomingShares.find((item) => item.profileId === id) ?? null;
  }

  isIncomingShare(id: string): boolean {
    return Boolean(this.incomingShareFor(id));
  }

  ownedProfiles(): Profile[] {
    return this.state.profiles.filter((profile) => !this.isIncomingShare(profile.id));
  }

  acceptIncomingShare(ownerId: string, profile: Profile): Profile {
    if (this.state.profiles.some((item) => item.id === profile.id && !this.isIncomingShare(item.id))) {
      this.switchProfile(profile.id);
      return this.state.profiles.find((item) => item.id === profile.id) ?? profile;
    }
    const next = ensureCurrentText(withProgress(normalizeSubjectProgress(profile)), corpus);
    const incomingShares = [
      ...this.state.incomingShares.filter((item) => item.profileId !== next.id),
      { ownerId, profileId: next.id },
    ];
    this.state = {
      ...this.state,
      profiles: [...this.state.profiles.filter((item) => item.id !== next.id), next],
      incomingShares,
      currentProfileId: next.id,
    };
    this.persist();
    return next;
  }

  acceptIncomingSummary(
    summary: StudentSummary,
    ownerId: string,
    shareId: string,
    permission: "view" | "contribute",
    ownerDisplayName: string | null,
  ): Profile {
    const usedColors = this.state.profiles.map((profile) => profile.colorPairIndex);
    const created = createProfile(
      summary.name,
      summary.readingBand,
      usedColors,
      new Date(summary.createdAt),
      summary.colorPairIndex,
    );
    const normalized = normalizeSubjectProgress({
      ...created,
      id: summary.id,
      coins: summary.coins,
      achievements: summary.achievements,
      inventory: summary.inventory,
    });
    const subjectProgress = applySummaryLevels(normalized.subjectProgress!, summary);
    const next = ensureCurrentText(
      withProgress({
        ...normalized,
        currentTextId: summary.subjects.reading.currentTextId,
        spellingWordId: summary.subjects.spelling.currentTextId ?? undefined,
        subjectProgress,
      }),
      corpus,
    );
    this.state = {
      ...this.state,
      profiles: [...this.state.profiles.filter((profile) => profile.id !== next.id), next],
      incomingShares: [
        ...this.state.incomingShares.filter((share) => share.profileId !== next.id),
        { ownerId, profileId: next.id, shareId, permission, ownerDisplayName },
      ],
      currentProfileId: next.id,
    };
    this.persist();
    return next;
  }

  applyRemoteSummaries(students: StudentSummary[], shares: ShareRecord[] = []): void {
    const existing = new Map(this.state.profiles.map((profile) => [profile.id, profile]));
    const usedColors = this.state.profiles.map((profile) => profile.colorPairIndex);
    const profiles = students.map((student) => {
      const current = existing.get(student.id);
      const readingCurrentTextId = student.subjects.reading.currentTextId;
      const mathCurrentTextId = student.subjects.math.currentTextId;
      const spellingWordId = student.subjects.spelling.currentTextId ?? undefined;
      if (!current) {
        const created = createProfile(
          student.name,
          student.readingBand,
          usedColors,
          new Date(student.createdAt),
          student.colorPairIndex,
        );
        const normalized = normalizeSubjectProgress({
          ...created,
          id: student.id,
          coins: student.coins,
          achievements: student.achievements,
          inventory: student.inventory,
        });
        const subjectProgress = applySummaryLevels(normalized.subjectProgress!, student);
        return ensureCurrentText(
          withProgress({
            ...normalized,
            currentTextId: readingCurrentTextId,
            spellingWordId,
            subjectProgress: {
              ...subjectProgress,
              math: { ...subjectProgress.math, currentTextId: mathCurrentTextId },
            },
          }),
          corpus,
        );
      }
      const normalized = normalizeSubjectProgress(current);
      const subjectProgress = applySummaryLevels(normalized.subjectProgress!, student);
      return ensureCurrentText(
        withProgress({
          ...normalized,
          currentTextId: readingCurrentTextId,
          spellingWordId,
          name: student.name,
          colorPairIndex: student.colorPairIndex,
          band: student.readingBand,
          coins: student.coins,
          achievements: student.achievements,
          inventory: student.inventory,
          subjectProgress: {
            ...subjectProgress,
            math: { ...subjectProgress.math, currentTextId: mathCurrentTextId },
          },
        }),
        corpus,
      );
    });
    this.importState(
      {
        ...this.state,
        profiles,
        incomingShares: shares.flatMap((share) =>
          share.ownerId
            ? [
                {
                  ownerId: share.ownerId,
                  profileId: share.studentId,
                  shareId: share.shareId,
                  permission: share.permission,
                  ownerDisplayName: share.ownerDisplayName,
                },
              ]
            : [],
        ),
        currentProfileId: profiles.some((profile) => profile.id === this.state.currentProfileId)
          ? this.state.currentProfileId
          : (profiles[0]?.id ?? null),
      },
      true,
    );
  }

  stopReceiving(id: string): void {
    const share = this.incomingShareFor(id);
    if (!share) return;
    const profiles = this.state.profiles.filter((profile) => profile.id !== id);
    const currentProfileId =
      this.state.currentProfileId === id ? (profiles[0]?.id ?? null) : this.state.currentProfileId;
    this.state = {
      ...this.state,
      profiles,
      incomingShares: this.state.incomingShares.filter((item) => item.profileId !== id),
      currentProfileId,
    };
    this.persist(true);
  }

  removeProfile(id: string): void {
    if (this.isIncomingShare(id)) {
      this.stopReceiving(id);
      return;
    }
    const profiles = this.state.profiles.filter((profile) => profile.id !== id);
    const currentProfileId =
      this.state.currentProfileId === id ? (profiles[0]?.id ?? null) : this.state.currentProfileId;
    this.state = {
      ...this.state,
      profiles: profiles.map((profile) =>
        profile.id === currentProfileId ? ensureCurrentText(profile, corpus) : profile,
      ),
      currentProfileId,
    };
    this.persist(false, "settingsChanged", [
      {
        eventId: createId(),
        type: "student.metadata.updated",
        studentId: id,
        occurredAt: new Date().toISOString(),
        payload: { archived: true },
      },
    ]);
  }

  switchProfile(id: string): boolean {
    const profile = this.state.profiles.find((item) => item.id === id);
    if (!profile) return false;
    this.state = {
      ...this.state,
      currentProfileId: id,
      profiles: this.state.profiles.map((item) => (item.id === id ? ensureCurrentText(item, corpus) : item)),
    };
    this.persist(false, "profileSwitch");
    return true;
  }

  record(result: ResultKind): { awardedCoin: boolean; awardedCoins: number } {
    const profile = this.currentProfile;
    const text = this.currentText;
    if (!profile || !text) return { awardedCoin: false, awardedCoins: 0 };
    let next = recordAndPickNext(profile, text, result, corpus);
    const readingProgress = next.subjectProgress?.reading;
    if (readingProgress) {
      next = {
        ...next,
        subjectProgress: {
          ...next.subjectProgress!,
          reading: {
            ...readingProgress,
            level: next.level,
            boostLevel: next.boostLevel,
            correctStreak: next.correctStreak,
            wrongStreak: next.wrongStreak,
            maxCorrectStreak: next.maxCorrectStreak,
            correctAnswers: readingProgress.correctAnswers + (result === "right" || result === "wayTooEasy" ? 1 : 0),
            events: [...readingProgress.events, { id: createId(), result, at: new Date().toISOString() }],
          },
        },
      };
    }
    const credit = result === "right" ? 1 : result === "wrong" ? 0.5 : 0;
    const reward = awardCoins(next, credit);
    next = reward.profile;
    const awardedCoins = reward.awardedCoins;
    const operations: SyncOperation[] = [
      {
        eventId: createId(),
        type: "answer.recorded",
        studentId: profile.id,
        occurredAt: new Date().toISOString(),
        payload: {
          subject: "reading",
          textId: text.id,
          itemId: text.id,
          result,
          level: text.level,
          studentLevel: next.level,
          studentBoostLevel: next.boostLevel,
          levelEpoch: next.subjectProgress?.reading.levelEpoch ?? 0,
        },
      },
      {
        eventId: createId(),
        type: "subject.current.updated",
        studentId: profile.id,
        occurredAt: new Date().toISOString(),
        payload: { subject: "reading", currentTextId: next.currentTextId },
      },
    ];
    if (awardedCoins > 0) {
      operations.push({
        eventId: createId(),
        type: "coins.granted",
        studentId: profile.id,
        occurredAt: new Date().toISOString(),
        payload: { delta: awardedCoins, reason: "answer" },
      });
    }
    this.replaceProfile(next, "answerRecorded", operations);
    return { awardedCoin: awardedCoins > 0, awardedCoins };
  }

  nextMathQuestion(excludeId?: string): MathQuestion | null {
    const progress = this.currentProfile?.subjectProgress?.math;
    if (!progress) return null;
    const level = progress.level;
    const shouldReview = progress.mathReviewCount >= progress.mathReviewInterval && level > 1;
    if (!shouldReview) return questionForLevel(level, progress, Math.random, excludeId);
    const wrongIds = Object.keys(progress.mathWrong).filter((id) => progress.mathWrong[id] === 0);
    const roll = Math.random();
    let reviewLevel = level - 1;
    if (roll >= 0.7 && wrongIds.length > 0) {
      const id = wrongIds[Math.floor(Math.random() * wrongIds.length)];
      const [, operation, left, right] = id.split("-");
      if (operation && left && right) {
        const question = questionFromPair(Number(id.split("-")[0]), { left: Number(left), right: Number(right) });
        if (question.id !== excludeId) return questionWithHint(question, progress);
      }
    } else if (roll >= 0.6) {
      reviewLevel = Math.max(1, level - 3);
    } else if (roll >= 0.4) {
      reviewLevel = Math.max(1, level - 2);
    }
    return questionForLevel(reviewLevel, progress, Math.random, excludeId);
  }

  recordMath(
    questionOrResult: MathQuestion | ResultKind | undefined,
    maybeResult?: ResultKind,
  ): { awardedCoin: boolean; awardedCoins: number; leveledUp: number | null } {
    const question = typeof questionOrResult === "object" ? questionOrResult : undefined;
    const result = typeof questionOrResult === "string" ? questionOrResult : (maybeResult ?? "skip");
    const profile = this.currentProfile;
    const progress = profile?.subjectProgress?.math;
    if (!profile || !progress) return { awardedCoin: false, awardedCoins: 0, leveledUp: null };
    const isCorrect = result === "right" || result === "wayTooEasy";
    const nextStreak = isCorrect ? progress.correctStreak + 1 : 0;
    const seen = question
      ? { ...progress.mathSeen, [question.id]: (progress.mathSeen[question.id] ?? 0) + 1 }
      : progress.mathSeen;
    const wrong = { ...progress.mathWrong };
    if (question) {
      if (result === "wrong") wrong[question.id] = 0;
      if (isCorrect && wrong[question.id] !== undefined) {
        wrong[question.id] += 1;
        if (wrong[question.id] >= 2) delete wrong[question.id];
      }
    }
    const nextEvents = [
      ...progress.events,
      { id: createId(), result, at: new Date().toISOString(), ...(question ? { itemId: question.id } : {}) },
    ];
    const nextProgressBase: SubjectProgress = {
      ...progress,
      mathSeen: seen,
      mathWrong: wrong,
      mathHintHidden:
        question && (result === "right" || result === "wrong")
          ? updateMathHint(question, progress, result)
          : progress.mathHintHidden,
      mathReviewCount: question && question.level !== progress.level ? 0 : progress.mathReviewCount + 1,
      mathReviewInterval:
        question && question.level !== progress.level ? 2 + Math.floor(Math.random() * 3) : progress.mathReviewInterval,
      events: nextEvents,
    };
    const nextLevel =
      isCorrect && mathLevelReady(progress.level, nextProgressBase)
        ? Math.min(100, progress.level + 1)
        : progress.level;
    const nextProgress: SubjectProgress = {
      ...nextProgressBase,
      level: nextLevel,
      boostLevel: nextLevel,
      correctStreak: nextLevel > progress.level ? 0 : nextStreak,
      wrongStreak: isCorrect ? 0 : result === "wrong" ? progress.wrongStreak + 1 : progress.wrongStreak,
      maxCorrectStreak: Math.max(progress.maxCorrectStreak, nextStreak),
      correctAnswers: progress.correctAnswers + (isCorrect ? 1 : 0),
      events: nextEvents,
    };
    const credit = result === "right" ? 1 : result === "wrong" ? 0.5 : 0;
    const reward = awardCoins(profile, credit);
    this.replaceProfile(
      {
        ...reward.profile,
        subjectProgress: { ...reward.profile.subjectProgress!, math: nextProgress },
      },
      "answerRecorded",
      [
        {
          eventId: createId(),
          type: "answer.recorded",
          studentId: profile.id,
          occurredAt: new Date().toISOString(),
          payload: {
            subject: "math",
            itemId: question?.id,
            result,
            level: question?.level ?? progress.level,
            studentLevel: nextLevel,
            studentBoostLevel: nextLevel,
            levelEpoch: progress.levelEpoch ?? 0,
          },
        },
        {
          eventId: createId(),
          type: "subject.current.updated",
          studentId: profile.id,
          occurredAt: new Date().toISOString(),
          payload: { subject: "math", currentTextId: nextProgress.currentTextId },
        },
        ...(reward.awardedCoins > 0
          ? [
              {
                eventId: createId(),
                type: "coins.granted" as const,
                studentId: profile.id,
                occurredAt: new Date().toISOString(),
                payload: { delta: reward.awardedCoins, reason: "answer" as const },
              },
            ]
          : []),
      ],
    );
    return {
      awardedCoin: reward.awardedCoins > 0,
      awardedCoins: reward.awardedCoins,
      leveledUp: nextLevel > progress.level ? nextLevel : null,
    };
  }

  recordSpelling(
    wordId: string,
    result: ResultKind,
  ): { awardedCoin: boolean; awardedCoins: number; leveledUp: number | null } {
    const profile = this.currentProfile;
    const progress = profile?.subjectProgress?.spelling;
    if (!profile || !progress) return { awardedCoin: false, awardedCoins: 0, leveledUp: null };
    const isCorrect = result === "right" || result === "wayTooEasy";
    const nextStreak = isCorrect ? progress.correctStreak + 1 : 0;
    const nextItemCorrect = {
      ...progress.itemCorrect,
      [wordId]: (progress.itemCorrect[wordId] ?? 0) + (isCorrect ? 1 : 0),
    };
    const currentLevelWords = SPELLING_CURRICULUM[progress.level - 1]?.words ?? [];
    const levelMastered = currentLevelWords.every((word) => (nextItemCorrect[word] ?? 0) >= 2);
    const nextLevel =
      isCorrect && nextStreak >= currentLevelWords.length && levelMastered
        ? Math.min(100, progress.level + 1)
        : progress.level;
    const nextProgress: SubjectProgress = {
      ...progress,
      level: nextLevel,
      boostLevel: nextLevel,
      correctStreak: nextLevel > progress.level ? 0 : nextStreak,
      wrongStreak: isCorrect ? 0 : result === "wrong" ? progress.wrongStreak + 1 : progress.wrongStreak,
      maxCorrectStreak: Math.max(progress.maxCorrectStreak, nextStreak),
      correctAnswers: progress.correctAnswers + (isCorrect ? 1 : 0),
      itemCorrect: nextItemCorrect,
      events: [...progress.events, { id: createId(), result, at: new Date().toISOString(), itemId: wordId }],
    };
    const credit = result === "right" ? 1 : result === "wrong" ? 0.5 : 0;
    const reward = awardCoins(profile, credit);
    this.replaceProfile(
      {
        ...reward.profile,
        subjectProgress: { ...reward.profile.subjectProgress!, spelling: nextProgress },
      },
      "answerRecorded",
      [
        {
          eventId: createId(),
          type: "answer.recorded",
          studentId: profile.id,
          occurredAt: new Date().toISOString(),
          payload: {
            subject: "spelling",
            itemId: wordId,
            result,
            level: progress.level,
            studentLevel: nextLevel,
            studentBoostLevel: nextLevel,
            levelEpoch: progress.levelEpoch ?? 0,
          },
        },
        ...(reward.awardedCoins > 0
          ? [
              {
                eventId: createId(),
                type: "coins.granted" as const,
                studentId: profile.id,
                occurredAt: new Date().toISOString(),
                payload: { delta: reward.awardedCoins, reason: "answer" as const },
              },
            ]
          : []),
      ],
    );
    return {
      awardedCoin: reward.awardedCoins > 0,
      awardedCoins: reward.awardedCoins,
      leveledUp: nextLevel > progress.level ? nextLevel : null,
    };
  }

  buyItem(itemId: string): boolean {
    const profile = this.currentProfile;
    const item = shopItemById(itemId);
    if (!profile || !item) return false;
    if (profile.inventory.includes(itemId) || profile.coins < item.cost) return false;
    this.replaceProfile(
      {
        ...profile,
        coins: profile.coins - item.cost,
        inventory: [...profile.inventory, itemId],
      },
      "settingsChanged",
      [
        {
          eventId: createId(),
          type: "purchase.completed",
          studentId: profile.id,
          occurredAt: new Date().toISOString(),
          payload: { itemId },
        },
      ],
    );
    return true;
  }

  setCurrentLocation(locationId: string): void {
    const profile = this.currentProfile;
    if (!profile || !profile.unlockedLocations.includes(locationId)) return;
    if (profile.currentLocationId === locationId) return;
    this.replaceProfile({ ...profile, currentLocationId: locationId });
  }

  setItemPosition(itemId: string, x: number, y: number): void {
    const profile = this.currentProfile;
    if (!profile || !profile.inventory.includes(itemId)) return;
    const nextX = Math.min(0.96, Math.max(0.04, x));
    const nextY = Math.min(0.96, Math.max(0.04, y));
    this.replaceProfile({
      ...profile,
      itemPositions: { ...profile.itemPositions, [itemId]: { x: nextX, y: nextY } },
    });
  }

  unlockInstructor(): void {
    this.instructorUnlocked = true;
    this.sessionUnlocked = true;
    this.persistInstructorUnlocked();
  }

  lockInstructor(): void {
    if (!this.hasPasscode()) return;
    this.instructorUnlocked = false;
    this.persistInstructorUnlocked();
  }

  unlockInstructorSession(): void {
    if (this.sessionUnlocked) return;
    this.sessionUnlocked = true;
    this.dispatchEvent(new Event("change"));
  }

  lockInstructorSession(): void {
    if (!this.sessionUnlocked) return;
    this.sessionUnlocked = false;
    this.dispatchEvent(new Event("change"));
  }

  hasPasscode(): boolean {
    return Boolean(this.state.passcode);
  }

  isLocked(): boolean {
    return this.hasPasscode() && !this.instructorUnlocked;
  }

  needsInstructorUnlock(): boolean {
    return this.isLocked() && !this.sessionUnlocked;
  }

  clearPasscode(): void {
    this.state = { ...this.state, passcode: null };
    this.instructorUnlocked = true;
    this.sessionUnlocked = true;
    this.persistInstructorUnlocked();
    this.persist();
  }

  verifyPasscode(value: string): boolean {
    return this.state.passcode === value;
  }

  isValidPasscode(value: string): boolean {
    return new RegExp(`^\\d{${PASSCODE_LENGTH}}$`).test(value);
  }

  setPasscode(value: string): boolean {
    if (!this.isValidPasscode(value)) return false;
    this.state = { ...this.state, passcode: value };
    this.persist();
    return true;
  }

  exportJson(): string {
    return JSON.stringify(this.state, null, 2);
  }

  importSharedProfile(incoming: Profile): Profile {
    const normalized = normalizeImportedProfile(incoming);
    const profile = ensureCurrentText(withProgress({ ...normalized, id: createId() }), corpus);
    this.state = {
      ...this.state,
      profiles: [...this.state.profiles, profile],
      currentProfileId: profile.id,
    };
    this.persist();
    return profile;
  }

  replaceSharedProfile(id: string, incoming: Profile): Profile {
    const existing = this.state.profiles.find((profile) => profile.id === id);
    if (!existing) {
      return this.importSharedProfile(incoming);
    }
    const normalized = normalizeImportedProfile(incoming);
    const profile = ensureCurrentText(
      withProgress({
        ...normalized,
        id: existing.id,
        createdAt: existing.createdAt,
      }),
      corpus,
    );
    this.state = {
      ...this.state,
      profiles: this.state.profiles.map((item) => (item.id === existing.id ? profile : item)),
      currentProfileId: existing.id,
    };
    this.persist();
    return profile;
  }

  importState(next: AppState, skipCloud = false): void {
    this.state = this.normalizeState(next);
    this.persist(skipCloud);
  }

  /** Another tab already wrote localStorage. Update memory without writing again. */
  adoptPersisted(next: AppState): void {
    this.state = this.normalizeState(next);
    this.dispatchEvent(new Event("change"));
  }

  persistedStorage(): StorageLike {
    return this.storage;
  }

  private normalizeState(next: AppState): AppState {
    const profiles = next.profiles.map((profile) =>
      ensureCurrentText(withProgress(normalizeSubjectProgress(profile)), corpus),
    );
    const currentProfileId = profiles.some((profile) => profile.id === next.currentProfileId)
      ? next.currentProfileId
      : (profiles[0]?.id ?? null);
    return {
      ...next,
      profiles,
      currentProfileId,
      incomingShares: next.incomingShares ?? [],
    };
  }

  wipeAll(): void {
    this.instructorUnlocked = false;
    this.sessionUnlocked = false;
    this.storage.removeItem(INSTRUCTOR_UNLOCKED_KEY);
    clearState(this.storage);
    this.state = loadState(this.storage);
    this.dispatchEvent(new Event("change"));
    this.dispatchEvent(new CustomEvent("persist", { detail: { skipCloud: true, operations: [] } }));
  }

  private persistInstructorUnlocked(): void {
    if (this.instructorUnlocked) {
      this.storage.setItem(INSTRUCTOR_UNLOCKED_KEY, "1");
    } else {
      this.storage.removeItem(INSTRUCTOR_UNLOCKED_KEY);
    }
    this.dispatchEvent(new Event("change"));
  }
}

const browserStorage = (): StorageLike => {
  if (typeof localStorage === "undefined") {
    const data = new Map<string, string>();
    return {
      getItem: (key) => data.get(key) ?? null,
      setItem: (key, value) => {
        data.set(key, value);
      },
      removeItem: (key) => {
        data.delete(key);
      },
    };
  }
  return localStorage;
};

export const appStore = new AppStore(browserStorage());
